import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { ModalService, ModalSettings } from 'mbs-ui-kit';
import { SortParams } from 'mbs-ui-kit/models/sort';
import { SmartSearchModel } from 'mbs-ui-kit/smart-search/models';
import { ToastService } from 'mbs-ui-kit/toast/toast.service';
import { SelectLicenseParams } from 'mbs-ui-shared@components/components/models/license.model';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { ComputerTagsFacade } from 'mbs-ui-shared@facades/computer.tags.facade';
import { PermissionsEnum, getPagingParams, getSortParams } from 'mbs-ui-shared@models';
import Computer, {
  AgentType,
  AutoUpdatePayload,
  ComputerTagDto,
  ComputersHealthFilterType,
  ComputersMode,
  ComputersResponse,
  GetComputersParams,
  PlanSettingsReportParams,
  RmmCeLimit,
  UpdateAgentSettings
} from 'mbs-ui-shared@models/Computer';
import { ComputerAvailableVersions } from 'mbs-ui-shared@models/ComputersModals';
import { ConnectionType } from 'mbs-ui-shared@models/ConnectionType';
import { InstallationAgents, getNeededBuildType } from 'mbs-ui-shared@models/InstallAgent';
import { BackupSidePanelTab } from 'mbs-ui-shared@models/backup/sidepanel-tab';
import { AvailableEdition, BackupAgentSettings } from 'mbs-ui-shared@models/edit-account-modal-models';
import { DestinationForEditAccount } from 'mbs-ui-shared@models/editAccountModalModels';
import { InstallBuildsType } from 'mbs-ui-shared@models/rm/remote-command-model';
import { SidepanelRouteType } from 'mbs-ui-shared@models/sidepanel';
import { AsyncRemoteCommandService } from 'mbs-ui-shared@services/async-remote-command/async-remote-command.service';
import { ComputersAbstractService } from 'mbs-ui-shared@services/computers.service';
import { RmCommandsWrapper } from 'mbs-ui-shared@services/rm-commands.wrapper';
import { versionCompare } from 'mbs-ui-shared@utils/version-compare';
import { ComputersFiltersWrapper } from 'mbs-ui-shared@wrappers/computer.filters.wrapper';
import { EMPTY, Observable, combineLatest, filter, interval, map, noop, of, scan, switchMap, take, tap } from 'rxjs';
import { ComputersStore } from '../store/computers/computers.store';
import { ComputersStatisticStore } from '../store/computersStatistic/computersStatistic.store';
import { GroupOperationIndicator } from './computers.facade.utils';

const pollingDelay = 2000;
const pollingLimit = 6;

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class ComputersFacade {
  private computersStore = inject(ComputersStore);
  private statisticStore = inject(ComputersStatisticStore);
  private computersService = inject(ComputersAbstractService);
  private rmCommands = inject(RmCommandsWrapper);
  private toast = inject(ToastService);
  private i18nPipe = inject(I18NextPipe);
  private filtersWrapper = inject(ComputersFiltersWrapper);
  private modalService = inject(ModalService);
  private asyncCommands = inject(AsyncRemoteCommandService);
  private ability = inject(AbilityService);
  private tagsFacade = inject(ComputerTagsFacade);
  private injector = inject(Injector);

  // shared part
  public computerAll$ = toObservable(this.computersStore.all);
  public computerLoading$ = toObservable(this.computersStore.loading);
  public computersBulkLoading$ = toObservable(this.computersStore.bulkLoading);

  public recoveryStatusComputerHid = signal<string>(null);

  // statistic (signals)
  readonly statistic = this.statisticStore.statistic;
  readonly statisticLoading = this.statisticStore.statisticLoading;
  readonly statisticLoaded = this.statisticStore.statisticLoaded;
  readonly statisticOneComputerExist = this.statisticStore.oneComputerExist;
  readonly statisticOneComputerLoading = this.statisticStore.oneComputerExistLoading;
  readonly statisticOneComputerLoaded = this.statisticStore.oneComputerExistLoaded;
  readonly statisticOneComputerExistLoaded = this.statisticStore.statisticOneComputerExistLoaded;

  // applications
  public applicationsCountAfterInit = computed(() =>
    this.computersStore.applicationsCountInitiated() ? this.computersStore.applicationsCount() : null
  );
  public applicationsCountAfterInit$ = toObservable(this.computersStore.applicationsCountInitiated).pipe(
    filter((initiated) => initiated),
    switchMap(() => toObservable(this.computersStore.applicationsCount, { injector: this.injector }))
  );

  // signals
  filtersValid = signal(true);
  computersTable = computed(() => (this.filtersValid() ? this.computersStore.currentPageData() : []));

  // paging
  mode = this.computersStore.mode;
  modeInitiated = this.computersStore.modeInitiated;
  currentPageNumber = this.computersStore.currentPageNumber;
  currentPerPageSetting = this.computersStore.perPage;
  currentPageIds = this.computersStore.currentPageIds;
  currentPageData = this.computersStore.currentPageData;

  filters = this.computersStore.allFilters;
  filterSearchModel = this.computersStore.filterSearchModel;
  filterCompany = this.computersStore.filterCompany;
  filterHealthType = this.computersStore.filterHealthType;
  filterHealth = this.computersStore.filterHealth;
  filterHidden = this.computersStore.filterHidden;
  filterInitiated = this.computersStore.filterInitiated;
  sortSettings = this.computersStore.sortSettings;
  hasFilters = this.computersStore.hasFilters;
  total = this.computersStore.totalItems;
  pagingLoading = this.computersStore.pagingLoading;
  pagingLoaded = this.computersStore.pagingLoaded;
  pagingHasError = this.computersStore.pagingHasError;

  mode$ = toObservable(this.computersStore.mode);
  modeInitiated$ = toObservable(this.computersStore.modeInitiated);
  currentPageNumber$ = toObservable(this.computersStore.currentPageNumber);
  currentPerPageSetting$ = toObservable(this.computersStore.perPage);
  currentPageIds$ = toObservable(this.computersStore.currentPageIds);
  currentPageData$ = toObservable(this.computersStore.currentPageData);

  filters$ = toObservable(this.computersStore.allFilters);
  filterSearchModel$ = toObservable(this.computersStore.filterSearchModel);
  filterCompany$ = toObservable(this.computersStore.filterCompany);
  filterHealthType$ = toObservable(this.computersStore.filterHealthType);
  filterHealth$ = toObservable(this.computersStore.filterHealth);
  filterHidden$ = toObservable(this.computersStore.filterHidden);
  filterInitiated$ = toObservable(this.computersStore.filterInitiated);
  sortSettings$ = toObservable(this.computersStore.sortSettings);
  filterSortSettings$ = combineLatest([this.filters$, this.sortSettings$]);
  hasFilters$ = toObservable(this.computersStore.hasFilters);
  total$ = toObservable(this.computersStore.totalItems);
  pagingLoading$ = toObservable(this.computersStore.pagingLoading);
  pagingLoaded$ = toObservable(this.computersStore.pagingLoaded);
  pagingHasError$ = toObservable(this.computersStore.pagingHasError);

  currentPageRequestParams$ = (skipSorting = false, skipPaging = false, skipHealth = false) =>
    toObservable(this.computersStore.requestParams, { injector: this.injector }).pipe(
      switchMap((params) =>
        this.filtersWrapper.getComputersHttpParams(
          params.filters,
          skipSorting ? undefined : getSortParams(params.sorting),
          skipPaging ? undefined : getPagingParams(params.paging),
          skipHealth
        )
      )
    );

  changeCounterHash$ = toObservable(this.computersStore.changeCounterHash);

  // data
  getDataLoading = this.computersStore.dataLoading;
  getDataLoaded = this.computersStore.dataLoaded;
  getDataTotal = this.computersStore.dataTotal;
  getData = this.computersStore.computersData;
  getDataCount = this.computersStore.dataCount;
  getAllDataLoaded = this.computersStore.allDataLoaded;
  getDataHasError = this.computersStore.dataHasError;

  getDataLoading$ = toObservable(this.computersStore.dataLoading);
  getDataLoaded$ = toObservable(this.computersStore.dataLoaded);
  getDataTotal$ = toObservable(this.computersStore.dataTotal);
  getData$ = (withLoad = false) => {
    withLoad && this.loadData();
    return toObservable(this.computersStore.computersData, { injector: this.injector });
  };
  getDataCount$ = toObservable(this.computersStore.dataCount);
  getAllDataLoaded$ = toObservable(this.computersStore.allDataLoaded);
  getDataHasError$ = toObservable(this.computersStore.dataHasError);

  // combined
  getDataEntityLoading = this.computersStore.dataEntityLoading;
  getPagingEntityLoading = this.computersStore.pagingEntityLoading;
  getDataWhenLoaded = computed(() => (this.getDataLoaded() ? this.getData() : []));
  getComputersNotFound = this.computersStore.computersNotFound;

  getDataEntityLoading$ = toObservable(this.computersStore.dataEntityLoading);
  getPagingEntityLoading$ = toObservable(this.computersStore.pagingEntityLoading);
  getDataWhenLoaded$ = combineLatest([this.getData$(), this.getDataLoaded$]).pipe(
    filter(([, loaded]) => loaded),
    map(([data]) => data)
  );
  getComputersNotFound$ = toObservable(this.computersStore.computersNotFound);

  /**
   * Retrieves a computer by its HID (Computer ID).
   * @param {string} hid - The ID of the computer to retrieve.
   * @param {boolean} directly - A boolean flag indicating whether to fetch the computer directly or through the store.
   *                    If true, fetches the computer directly and updates the store with the retrieved data.
   *                    If false, selects the computer from the store.
   * @return {Observable<Computer>} An Observable that emits the retrieved Computer object.
   */
  getByHid$(hid: string, directly = false): Observable<Computer> {
    return directly
      ? this.getComputerByHid(hid).pipe(tap((computer) => this.setComputersData([computer])))
      : toObservable(this.computersStore.entityByHid(hid), { injector: this.injector });
  }

  /**
   * Retrieves an array of computers by their HIDs (Computer IDs).
   * @param {string[]} hids - An array of computer IDs (HIDs) to retrieve.
   * @return {Observable<Computer[]>} An observable that emits an array of Computer objects.
   */
  getByHids$(hids: string[]): Observable<Computer[]> {
    return toObservable(this.computersStore.entitiesByHids(hids), { injector: this.injector });
  }

  /**
   * Retrieves the missed status of a computer by its HID (Computer not found).
   * @param {string} hid - The ID of the computer.
   * @return {Observable<boolean>} An observable that emits a boolean indicating whether the computer is missed.
   */
  getMissedByHid$(hid: string): Observable<boolean> {
    return toObservable(this.computersStore.missedByHid(hid), { injector: this.injector });
  }

  /**
   * Retrieves a computer by its HID (Computer ID). Signal version
   * @param {string} hid - The ID of the computer to retrieve.
   * @return {Signal<Computer>} A signal with Computer object.
   */
  getByHid(hid: string) {
    return this.computersStore.entityByHid(hid);
  }

  /**
   * Retrieves an array of computers by their HIDs (Computer IDs). Signal version
   * @param {string[]} hids - An array of computer IDs (HIDs) to retrieve.
   * @return {Signal<Computer[]>} A signal with array of Computer objects.
   */
  getByHids(hids: string[]) {
    return this.computersStore.entitiesByHids(hids);
  }

  /**
   * Retrieves the missed status of a computer by its HID (Computer not found). Signal version
   * @param {string} hid - The ID of the computer.
   * @return {Signal<boolean>} A boolean signal indicating whether the computer is missed.
   */
  getMissedByHid(hid: string) {
    return this.computersStore.missedByHid(hid);
  }

  /**
   * Load computer data by hid in Computers store
   * @param {{hid: string, quiet: boolean, force: boolean}} params Parameters
   * @param {string} params.hid Computer Hid
   * @param {boolean} params.quiet Quiet loading of computer. Without  changes in 'loading' store field (without spinner)
   * @param {boolean} params.force Force loading data from the backend (even if the data is already in store)
   * @return {void}
   */
  loadComputerByHid(params: { hid: string; quiet?: boolean; force?: boolean }): void {
    this.computersStore.loadComputerByHid(params);
  }

  /**
   * Load computers data by hids in Computers store
   * @param {{hids: string[], force: boolean}} params Parameters
   * @param {string} params.hids Computers Hids
   * @param {string[]} params.includes Properties that contain resources. Leave empty to pick all
   * @param {boolean} params.force Force request data from the backend (even if the data is already in store)
   * @return {void}
   */
  loadComputersByHids(params: { hids: string[]; includes?: string[]; force?: boolean; loadDeleted?: boolean; loadHidden?: boolean }): void {
    this.computersStore.loadComputersByHids(params);
  }

  /**
   * Dispatches an action to set the computers data in the store.
   *
   * @param {Computer[]} computers - The array of computers to be set in the store.
   * @return {void}
   */
  setComputersData(computers: Computer[]): void {
    this.computersStore.setComputers(computers);
  }

  // paging part

  /**
   * Sets the mode (All, Backup, RMM) for the computers page.
   *
   * @param {ComputersMode} mode - The mode to set for the computers page.
   * @return {void}
   */
  setMode(mode: ComputersMode): void {
    this.computersStore.setMode({ mode });
  }

  loadPage(params: { pageNumber?: number; perPage?: number; isFullRefresh?: boolean }): void {
    this.filtersValid() && this.computersStore.loadPage(params);
  }

  clearPages(): void {
    this.computersStore.clearPages();
  }

  setFilterSearch(searchModel: SmartSearchModel): void {
    this.computersStore.setFilterSearch({ searchModel });
  }

  setFilterCompany(company: string): void {
    this.computersStore.setFilterCompany({ company });
  }

  setFilterHidden(hidden: boolean): void {
    this.computersStore.setFilterHidden({ hidden });
  }

  setFilterHealth(healthType: ComputersHealthFilterType): void {
    this.computersStore.setFilterHealth({ healthType });
  }

  setAllFilters(params: {
    searchModel?: SmartSearchModel;
    hidden?: boolean;
    healthType?: ComputersHealthFilterType;
    company?: string;
    apply?: boolean;
  }): void {
    this.computersStore.setAllFilters(params);
  }

  setSortSettings(sort: SortParams): void {
    this.computersStore.setSortSettings({ sort });
  }

  setPageNumber(pageNumber: number): void {
    this.computersStore.setPageNumber({ pageNumber });
  }

  setPerPageSetting(perPage: number): void {
    this.computersStore.setPerPageSetting({ perPage });
  }

  refresh(): void {
    this.loadPage({ isFullRefresh: true });
    this.currentPageRequestParams$(true, true, true)
      .pipe(take(1), untilDestroyed(this))
      .subscribe((filters) => this.loadStatistic({ filters, force: true }));
  }

  // data
  loadData(isFullRefresh = false, allowLoadMore = false, limit: number = null): void {
    this.computersStore.loadData(isFullRefresh, allowLoadMore, limit);
  }

  refreshData(): void {
    this.loadData(true);
  }

  clearData(): void {
    this.computersStore.clearData();
  }

  // statistic
  loadStatistic(params: { filters: GetComputersParams; silent?: boolean; force?: boolean }): void {
    this.statisticStore.loadStatistic(params);
  }

  checkOneComputerExist(force = false): void {
    this.statisticStore.checkOneComputerExist({ force });
  }

  // Service commands
  getComputers(params?: GetComputersParams): Observable<ComputersResponse> {
    return this.computersService.getComputers(params);
  }

  getComputerByHid(hid: string): Observable<Computer> {
    return this.computersService.getComputerByHid(hid);
  }

  setAutoUpdate(payload: AutoUpdatePayload): Observable<any> {
    return this.computersService.setComputersAutoUpdate(payload);
  }

  setComputerAutoUpdate(hid: string, enabled: boolean): Observable<any> {
    return this.computersService.setComputerAutoUpdate(hid, enabled);
  }

  setHidden(hid: string, hidden = true): Observable<any> {
    return this.computersService.setComputerHidden(hid, hidden);
  }

  setComputerDisplayName(hid: string, displayName: string): Observable<any> {
    return this.computersService.setComputerDisplayName(hid, displayName);
  }

  update(computer: Computer): Observable<any> {
    return this.computersService.updateComputer(computer);
  }

  delete(hid: string): Observable<any> {
    return this.computersService.deleteComputer(hid);
  }

  getConnectionUrl(computer: Computer, connectionType: ConnectionType): Observable<any> {
    return this.computersService.getConnectionUrl(computer, { connectionType });
  }

  isConnectionAllowed(hid: string, connectionType: ConnectionType, withoutBackup = false): Observable<{ result: boolean }> {
    return this.computersService.isConnectionAllowed(hid, connectionType, withoutBackup);
  }

  isPossibleToInstallAgent(computer: Computer, agent: AgentType): Observable<AgentType> {
    return of(!!getNeededBuildType(computer, agent)).pipe(
      map((possibleToInstall) => {
        if (!possibleToInstall) return null;
        return computer.apps.find((app) => app.online && InstallationAgents.includes(app.applicationId))?.applicationId ?? null;
      })
    );
  }

  installAgent(computer: Computer, agentType: AgentType, async = false): Observable<any> {
    const showUnableInstallToast = () => {
      const title = this.i18nPipe.transform('shared:computers.modals.unableInstallCaption');
      const agent = this.i18nPipe.transform(`shared:products.${agentType}`);
      const computerName = Computer.getComputerName(computer);
      const message = this.i18nPipe.transform('shared:computers.modals.unableInstall', { agent, computerName });
      this.toast.error(message, title);
    };

    return this.isPossibleToInstallAgent(computer, agentType).pipe(
      tap((installedAgent) => !installedAgent && showUnableInstallToast()),
      filter(Boolean),
      switchMap((installedAgent) => this.rmCommands.installPublicAgentByComputer({ computer, appId: agentType, installedAgent }, async))
    );
  }

  installAgentToComputers(computers: Computer[], agentType: AgentType, async = false): Observable<any> {
    const affectedComputers = computers.filter((computer) => computer.online);
    if (affectedComputers.length === 1) return this.installAgent(affectedComputers[0], agentType, async);
    return this.rmCommands.installPublicAgentByComputers(
      { computers: computers.filter((computer) => computer.online), appId: agentType },
      async
    );
  }

  uninstallAgents(computer: Computer, appIds: string[] | AgentType[]): Observable<any> {
    return this.rmCommands.uninstallAgentsByComputer({ computer, appIds });
  }

  updateAgent(computerHid: string, agentType: AgentType, versionType: InstallBuildsType): Observable<any> {
    return this.rmCommands.updateAgent({ computerHid, agentType, versionType });
  }

  bulkInstallAgent(filter: GetComputersParams, appId: AgentType, async = false): Observable<any> {
    return this.rmCommands.bulkInstallPublicAgent(filter, appId, async);
  }

  regenerateClientHID(computerHid: string, appId: AgentType): Observable<unknown> {
    return this.rmCommands.regenerateClientHID({ computerHid, appId });
  }

  getAvailableVersions(hid: string): Observable<ComputerAvailableVersions> {
    return this.computersService.availableVersions(hid);
  }

  newAgentVersionAvailable(hid: string, agentType: AgentType): Observable<InstallBuildsType | null> {
    return this.getAvailableVersions(hid).pipe(
      map((computer: ComputerAvailableVersions) => {
        let result: InstallBuildsType | null = null;
        const agent = (computer?.applications || []).find((agent) => agent.applicationId.toLowerCase() === agentType);
        if (agent?.builds && agent?.currentVersion) {
          result =
            Object.values(InstallBuildsType).find((buildType) => {
              const newVersion = agent.builds[buildType.toLowerCase()]?.version;
              return newVersion && versionCompare(newVersion, agent.currentVersion) > 0;
            }) || null;
        }
        return result;
      })
    );
  }

  planSettingsReport(params: PlanSettingsReportParams): Observable<boolean> {
    return this.computersService.planSettingsReport(params).pipe(map(() => true));
  }

  export(params?: GetComputersParams): Observable<any> {
    return this.computersService.export(params);
  }

  switchWebSettings(hid: string, state: boolean): Observable<{ ok: boolean }> {
    return this.rmCommands.switchWebSettings(hid, state);
  }

  getPlansHistory(
    hid: string,
    params: { PlanId?: string; Limit?: number; Offset?: number; FromUtc?: string; ToUtc?: string; IsAsync?: boolean }
  ): Observable<unknown> {
    return this.rmCommands.getPlansHistory(hid, params);
  }

  loadApplicationsCount(force?: boolean): void {
    this.computersStore.loadApplicationsCount({ force });
  }

  suggestUserName(hid: string, companyId: string, forceNew?: boolean): Observable<{ userId: string; userName: string }> {
    return this.computersService.suggestUserName(hid, companyId, forceNew);
  }

  groupAuthorizeComputers(hids: string[], userId?: string, companyId?: string): Observable<null> {
    return this.computersService.authorizeComputers({ hids, userId, companyId });
  }

  bulkAuthorizeComputers(filter: GetComputersParams, userId?: string, companyId?: string): Observable<null> {
    return this.computersService.authorizeComputers({ filter, userId, companyId });
  }

  authorizeComputer(hid: string, userId?: string, companyId?: string, assignLicense?: SelectLicenseParams): Observable<null> {
    return this.computersService.authorizeComputer(hid, userId, companyId, assignLicense);
  }

  getComputersCount(params: { type?: 'unsupported'; userIds?: string[] }, headers: { [header: string]: string }): Observable<number> {
    return this.computersService.getComputersCount(params, headers).pipe(map(({ total }) => total));
  }

  pollingComputersAndOpenSidePanel(sidePanel: SidepanelRouteType.Backup | SidepanelRouteType.RMM, hid?: string, limit?: number): void {
    const agent: AgentType = sidePanel === SidepanelRouteType.Backup ? AgentType.Backup : AgentType.RMM;
    const getData = hid
      ? this.getComputerByHid(hid)
      : this.getComputers({ limit: 1, offset: 0, appIds: [agent] }).pipe(
          map((res) => (res && res.total && res.data.length ? res.data[0] : null))
        );
    // polling
    interval(pollingDelay)
      .pipe(
        switchMap(() => getData),
        scan((acc, computer) => ({ acc: acc.acc + 1, computer }), { acc: 0, computer: undefined }),
        map(({ acc, computer }) => ({ computer, isLimitExceeded: acc >= (limit ?? pollingLimit) })),
        filter(({ computer, isLimitExceeded }) => (computer?.online && Computer.isAgentOnline(computer, agent)) || isLimitExceeded),
        take(1)
      )
      .subscribe(({ computer }) => this.openComputersSidepanelByHid(sidePanel, computer?.hid));
  }

  checkOneComputerHasOnlineAgent(agent: AgentType.Backup | AgentType.RMM, hid?: string): Observable<string> {
    return (
      hid
        ? this.getComputerByHid(hid)
        : this.getComputers({ limit: 1, offset: 0, appIds: [agent] }).pipe(
            map((res) => (res && res.total && res.data.length ? res.data[0] : null))
          )
    ).pipe(map((res) => (res && res.online && Computer.isAgentOnline(res, agent) ? res.hid : null)));
  }

  getComputerSidepanelByHidURL(
    sidePanel: SidepanelRouteType.Backup | SidepanelRouteType.RMM,
    hid?: string,
    activeTab?: BackupSidePanelTab,
    additionalParams?: Record<string, string>
  ): string {
    let sidePanelParams = '';
    if (hid) {
      const defaultTab = sidePanel === SidepanelRouteType.Backup ? `&tab=${BackupSidePanelTab.backupPlans}` : '';
      const tab = activeTab ? `&tab=${activeTab}` : defaultTab;
      sidePanelParams = `/${hid}?sidepanel=${sidePanel}${tab}`;
      Object.keys(additionalParams || {}).forEach((key) => {
        sidePanelParams += `&${key}=${additionalParams[key]}`;
      });
    }
    const path = sidePanel === SidepanelRouteType.RMM ? RoutingPath.ApRMMComputers : RoutingPath.ApComputers;
    return `${path}${sidePanelParams}`;
  }

  openComputersSidepanelByHid(sidePanel: SidepanelRouteType.Backup | SidepanelRouteType.RMM, hid?: string): void {
    window.location.href = this.getComputerSidepanelByHidURL(sidePanel, hid);
  }

  getAvailableEditionsById(hid: string, userId: string): Observable<AvailableEdition[]> {
    return this.computersService.getAvailableEditionsById(hid, userId);
  }

  getDestinationsByComputerHidAndUserId(hid: string, userId: string): Observable<DestinationForEditAccount[]> {
    return this.computersService.getDestinationsByComputerHidAndUserId(hid, userId);
  }

  getDestinationsPrefixListByUserId(hid: string, destinationId: string, userId: string): Observable<string[]> {
    return this.computersService.getDestinationsPrefixListByUserId(hid, destinationId, userId);
  }

  updateBackupAgentSettings(hid: string, settings: BackupAgentSettings): Observable<UpdateAgentSettings> {
    return this.computersService.updateBackupAgentSettings(hid, settings);
  }

  updateUser(hid: string, userId: string): Observable<any> {
    return this.computersService.updateUser(hid, userId);
  }

  addTags(tagIds: number[], hids?: string[], filter?: GetComputersParams): Observable<null> {
    return this.computersService.addTags(tagIds, hids, filter);
  }

  editTags(hids: string[], tagIds: number[]): Observable<null> {
    return this.computersService.editTags(hids, tagIds);
  }

  getTags(hid: string): Observable<ComputerTagDto[]> {
    return combineLatest([this.getByHid$(hid), this.tagsFacade.allTags$]).pipe(
      map(([computer, tags]) => {
        return computer?.tags.map((computerTag) => ({ ...computerTag, color: tags.find((tag) => tag.id === computerTag.id)?.color }));
      })
    );
  }

  invalidate(): void {
    combineLatest([this.getDataLoaded$, this.pagingLoaded$])
      .pipe(take(1))
      .subscribe(([dataLoaded, pagingLoaded]) => {
        dataLoaded && this.loadData(true);
        pagingLoaded && this.loadPage({ isFullRefresh: true });
      });
  }

  getComputerNotesByHid(hid: string): Observable<{ note: string }> {
    return this.computersService.getComputerNotesByHid(hid);
  }

  updateComputerNotesByHid(hid: string, payload: { note: string }): Observable<unknown> {
    return this.computersService.updateComputerNotesByHid(hid, payload);
  }

  handleGroupOperation(fullData: Observable<unknown>, config: (typeof GroupOperationIndicator.prototype)['config']): Observable<boolean> {
    const indicator = new GroupOperationIndicator(this.toast, this.modalService, this.i18nPipe, config, untilDestroyed(this));
    const intermediateData = this.asyncCommands.lastAsyncResult;
    return indicator.indicate(fullData, intermediateData);
  }

  checkRmmCeLimit(): Observable<RmmCeLimit> {
    if (!this.ability.can('read', PermissionsEnum.RmmCeEnabled)) return EMPTY;
    return this.computersService.checkRmmCeLimit();
  }

  showIsLocalInfoModal(): void {
    const settings: ModalSettings = {
      header: { title: this.i18nPipe.transform('shared:sidepanel-backup.unableUseQuickRestoreTitle') },
      footer: { okButton: { show: false }, cancelButton: { text: this.i18nPipe.transform('shared:buttons.close') } }
    };
    this.modalService.open(settings, this.i18nPipe.transform('shared:sidepanel-backup.unableUseQuickRestoreText')).then(noop).finally(noop);
  }
}
